import type { VehicleSide, VehicleType } from "@/lib/api/types"

// Quick Estimate 2 — the four required photo slots in capture order.
// Maps 1-to-1 to the canonical VehicleSide values the BE accepts.
export type ShotKey = "front" | "driver" | "passenger" | "rear"

export interface ShotMeta {
  key: ShotKey
  label: string
  side: VehicleSide
}

export const SHOT_SEQUENCE: ShotMeta[] = [
  { key: "front",     label: "Front of vehicle", side: "FRONT_CENTER" },
  { key: "driver",    label: "Driver side",      side: "LEFT_SIDE" },
  { key: "passenger", label: "Passenger side",   side: "RIGHT_SIDE" },
  { key: "rear",      label: "Rear of vehicle",  side: "REAR_CENTER" },
]

// Verdict the photo screen surfaces after the AI quality check.
// Each variant has one user-facing sentence (in copy.ts) — the
// system blames itself, never the user.
export type PhotoVerdict =
  | "good"
  | "too-close"
  | "too-dark"
  | "blurry"
  | "wrong-side"
  | "low-quality"

// A photo that's been taken, evaluated, and is ready (or being
// reviewed). Captured photos accumulate in state by ShotKey for the
// 4 required shots; close-ups are appended to a separate array.
export interface CapturedShot {
  // The local preview URL (createObjectURL) we render in the UI.
  // The CDN URL from the agent is what we send at submit time.
  previewUrl: string
  // Returned by agentsApi.evaluateImage — the URL the BE actually
  // stores. We keep it separate from previewUrl because previewUrl
  // is revoked on unmount; uploadedUrl persists across the session.
  uploadedUrl: string
  // BE-canonical side (may differ from the slot's expected side if
  // the user pointed the camera the wrong way — we surface "wrong
  // side?" in that case).
  detectedSide: VehicleSide
  qualityScore: number
  qualified: boolean
  qualityIssues: string[]
  // Verdict the UI shows after the photo comes back. We compute it
  // from {qualityScore, qualified, qualityIssues, detectedSide vs
  // expected side} in deriveVerdict() so the UI doesn't repeat that
  // logic.
  verdict: PhotoVerdict
  // How many times we've retaken THIS slot. After 2 fails on the
  // same slot, the UI offers "Use this photo" so the user isn't
  // trapped in a quality loop.
  retakeCount: number
  // Stamp so localStorage replays can drop stale entries.
  takenAt: number
  // What the AI read in this photo ("front bumper, driver side") — shown as
  // the "We see: …" line under "Got it" in the consumer flow. Absent when the
  // evaluation didn't return a description.
  description?: string
}

export interface VinDecodedVehicle {
  vin: string
  year: string | null
  make: string | null
  model: string | null
  trim: string | null
  // Body class from the VIN decoder. Drives which line-art reference
  // illustration the photo step shows. Null falls back to sedan
  // (still the most common car on US roads).
  type: VehicleType | null
  // Optional: hex color (e.g. "#1f2937") if the BE's VIN service
  // returns it. We render a small swatch on the review screen when
  // present. Null when unknown — falls back to a neutral swatch.
  colorHex?: string | null
}

// The reference-image system collapses the 15-value VehicleType union
// into a smaller set of body silhouettes we actually have artwork for.
// `coupe`/`wagon`/etc. share the sedan envelope; minivan/cargo share
// the van envelope. Edit ReferenceImage when you add a new family.
export type BodySilhouette = "sedan" | "suv" | "hatchback" | "pickup" | "van"

export function silhouetteFor(type: VehicleType | null | undefined): BodySilhouette {
  switch (type) {
    case "SUV":
    case "Crossover":
      return "suv"
    case "Hatchback":
      return "hatchback"
    case "Pickup":
      return "pickup"
    case "Van":
    case "MiniVan":
    case "CargoVan":
      return "van"
    // Sedan, Coupe, Convertible, StationWagon, Compact, CityCar,
    // SuperMini, Other → sedan envelope.
    default:
      return "sedan"
  }
}

// Canonical damage zones the user can tap on the top-down vehicle map.
// Match the granularity body shops actually quote against — door / panel
// level, not single bolts.
export type DamageZone =
  | "hood"
  | "front-bumper"
  | "front-fender-left"
  | "front-fender-right"
  | "front-door-left"
  | "front-door-right"
  | "rear-door-left"
  | "rear-door-right"
  | "quarter-panel-left"
  | "quarter-panel-right"
  | "trunk"
  | "rear-bumper"
  | "roof"
  | "windshield"

// Persisted shape — what we write to localStorage on every step
// transition so an interrupted user picks up exactly where they
// left off. We DON'T persist `previewUrl` (objectURLs are dead
// after a tab close); on hydration, previewUrl is rebuilt from
// uploadedUrl so images still render after a resume.
export interface ConsumerIntakeEstimateDraft {
  v: 2 // bumped from 1 — added damageZones, dropped persisted previewUrl
  vin: string
  vehicle: VinDecodedVehicle | null
  // Map of slot → captured shot. Slots without a photo are absent.
  shots: Partial<Record<ShotKey, CapturedShot>>
  closeUps: CapturedShot[]
  notes: string
  damageZones: DamageZone[]
  customer: {
    name: string
    phone: string
  }
  updatedAt: number
}

export const DRAFT_STORAGE_KEY = "etx_quick_estimate_2_draft_v2"

// Public consumer flow (/go/:shopId) draft — a SEPARATE key from the in-store
// advisor wizard so the two never collide on a shared device. Same draft
// shape + 14-day TTL.
export const CONSUMER_FLOW_DRAFT_KEY = "etx_consumer_flow_draft_v1"

// Returning-consumer tracking pointer. After a successful intake we stash the
// opaque trackToken here so a return visit can offer "resume your repair"
// without the SMS link. { shopId, estimateId, trackToken, ts }.
export interface ConsumerTrackingRef {
  shopId: number | string
  estimateId: number
  trackToken: string
  ts: number
}
export const CONSUMER_TRACKING_KEY = "etx_consumer_tracking_v1"

// Cached most-recent customer info — used by the send screen to
// offer a one-tap "Use last customer" pre-fill. Capped at one entry
// so repeated submissions overwrite. 30-min TTL by default.
export interface RecentCustomerCache {
  name: string
  phone: string
  ts: number
}
export const RECENT_CUSTOMER_KEY = "etx_quick_estimate_2_recent_customer_v1"
